// src/pages/BuatPengaduan.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./BuatPengaduan.css";

const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/zip", "application/x-zip-compressed",
  "application/x-rar-compressed", "application/vnd.rar",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];

const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function getFileTypeLabel(type) {
  if (type.startsWith("image/")) return { label: "IMG", color: "#6366f1" };
  if (type === "application/pdf") return { label: "PDF", color: "#ef4444" };
  if (type.includes("zip")) return { label: "ZIP", color: "#f59e0b" };
  if (type.includes("rar")) return { label: "RAR", color: "#f59e0b" };
  if (type.includes("word")) return { label: "DOC", color: "#3b82f6" };
  if (type.includes("excel") || type.includes("spreadsheet")) return { label: "XLS", color: "#10b981" };
  return { label: "TXT", color: "#64748b" };
}

// Komponen untuk satu item file
function FileItem({ file, index, preview, onRemove }) {
  const { label, color } = getFileTypeLabel(file.type);
  const isImage = file.type.startsWith("image/");

  return (
    <li className="bp-file-item" style={{ "--entry-delay": `${index * 60}ms` }}>
      {/* Thumbnail / Badge */}
      <div className="bp-file-thumb">
        {isImage && preview ? (
          <img src={preview} alt={file.name} className="bp-file-thumb-img" />
        ) : (
          <span className="bp-file-type-badge" style={{ background: color }}>
            {label}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="bp-file-info">
        <span className="bp-file-name" title={file.name}>{file.name}</span>
        <span className="bp-file-size">{formatBytes(file.size)}</span>
      </div>

      {/* Remove */}
      <button
        className="bp-file-remove"
        onClick={() => onRemove(index)}
        type="button"
        aria-label={`Hapus ${file.name}`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </li>
  );
}

const BuatPengaduan = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [trackingCode, setTrackingCode] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Step 1
  const [nama, setNama] = useState("");
  const [kontak, setKontak] = useState("");

  // Step 2
  const [judul, setJudul] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({}); // { filename+size: objectURL }
  const [dragOver, setDragOver] = useState(false);
  const [fileErrors, setFileErrors] = useState([]); // array of error strings

  // Loading & error
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Bersihkan object URL saat komponen unmount atau files berubah
  useEffect(() => {
    return () => {
      Object.values(previews).forEach(URL.revokeObjectURL);
    };
  }, []);

  /* ── File Helpers ──────────────────────────── */
  const getFileKey = (f) => `${f.name}-${f.size}`;

  const buildPreview = (file) => {
    if (!file.type.startsWith("image/")) return null;
    return URL.createObjectURL(file);
  };

  /* ── File Handlers ─────────────────────────── */
  const validateAndAdd = (newFiles) => {
    const errors = [];
    const valid = [];

    for (const file of newFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" — tipe file tidak didukung.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errors.push(`"${file.name}" — ukuran melebihi ${MAX_FILE_SIZE_MB} MB.`);
        continue;
      }
      valid.push(file);
    }

    setFiles((prev) => {
      // Deteksi duplikat
      const existingKeys = new Set(prev.map(getFileKey));
      const deduped = valid.filter((f) => {
        if (existingKeys.has(getFileKey(f))) {
          errors.push(`"${f.name}" — sudah dilampirkan.`);
          return false;
        }
        return true;
      });

      const combined = [...prev, ...deduped];
      if (combined.length > MAX_FILES) {
        const excess = combined.length - MAX_FILES;
        errors.push(`${excess} file diabaikan — batas maksimal ${MAX_FILES} file.`);
        const trimmed = combined.slice(0, MAX_FILES);
        // Buat previews untuk yang baru
        const newPreviews = {};
        trimmed.slice(prev.length).forEach((f) => {
          const url = buildPreview(f);
          if (url) newPreviews[getFileKey(f)] = url;
        });
        setPreviews((p) => ({ ...p, ...newPreviews }));
        return trimmed;
      }

      // Buat previews
      const newPreviews = {};
      deduped.forEach((f) => {
        const url = buildPreview(f);
        if (url) newPreviews[getFileKey(f)] = url;
      });
      setPreviews((p) => ({ ...p, ...newPreviews }));
      return combined;
    });

    setFileErrors(errors);
  };

  const handleFileChange = (e) => {
    validateAndAdd(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndAdd(Array.from(e.dataTransfer.files));
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => {
      const removed = prev[index];
      const key = getFileKey(removed);
      // Revoke object URL jika ada
      if (previews[key]) {
        URL.revokeObjectURL(previews[key]);
        setPreviews((p) => {
          const next = { ...p };
          delete next[key];
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
    setFileErrors([]);
  };

  /* ── Navigation ─────────────────────────────── */
  const handleNext = () => { if (step < 3) setStep(step + 1); };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  /* ── Submit ──────────────────────────────────── */
  const handleSubmit = async () => {
    setIsLoading(true);
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("nama_pelapor",  nama.trim() || "Anonim");
      formData.append("email_pelapor", kontak.trim());
      formData.append("judul",         judul.trim());
      formData.append("deskripsi",     deskripsi.trim());
      files.forEach((f) => formData.append("lampiran", f));

      const response = await fetch("/api/pengaduan/buat/", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMsg(data.message || "Gagal mengirim laporan. Coba lagi.");
        return;
      }

      setTrackingCode(data.kode_unik);
      setStep(3);
    } catch {
      setErrorMsg("Tidak dapat terhubung ke server. Periksa koneksi Anda.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(trackingCode);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleCreateNew = () => {
    Object.values(previews).forEach(URL.revokeObjectURL);
    setStep(1);
    setNama(""); setKontak("");
    setJudul(""); setDeskripsi("");
    setFiles([]); setPreviews({});
    setTrackingCode("");
    setErrorMsg(""); setFileErrors([]);
  };

  /* ── Render ─────────────────────────────────── */
  return (
    <div className="bp-wrapper">
      {/* NAVBAR */}
      <nav className="bp-navbar">
        <button className="bp-btn-back-nav" onClick={() => navigate(-1)}>← Kembali</button>
        <img src="/logolemhannas.png" alt="Logo" className="bp-logo" />
        <button className="bp-btn-login" onClick={() => navigate("/login")}>Login</button>
      </nav>

      <main className="bp-main">
        {/* Header */}
        <div className="bp-header">
          <h1 className="bp-title">Buat Laporan Pengaduan</h1>
          <p className="bp-subtitle">
            Isi form di bawah dengan lengkap agar laporan
            <br className="bp-br" />
            dapat diproses dengan cepat.
          </p>
        </div>

        {/* Stepper */}
        <div className="bp-stepper">
          <div className="bp-step-item">
            <div className={`bp-step-circle ${step > 1 ? "done" : step === 1 ? "active" : ""}`}>
              {step > 1 ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : "1"}
            </div>
            <span className={`bp-step-label ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>Informasi</span>
          </div>
          <div className={`bp-step-line ${step > 1 ? "done" : ""}`} />
          <div className="bp-step-item">
            <div className={`bp-step-circle ${step > 2 ? "done" : step === 2 ? "active" : ""}`}>
              {step > 2 ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : "2"}
            </div>
            <span className={`bp-step-label ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>Detail</span>
          </div>
          <div className={`bp-step-line ${step > 2 ? "done" : ""}`} />
          <div className="bp-step-item">
            <div className={`bp-step-circle ${step === 3 ? "done" : ""}`}>
              {step === 3 ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : "3"}
            </div>
            <span className={`bp-step-label ${step === 3 ? "active" : ""}`}>Selesai</span>
          </div>
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <>
            <div className="bp-card">
              <div className="bp-card-header">
                <span className="bp-card-accent" />
                <h2 className="bp-card-title">Informasi Pelapor</h2>
              </div>
              <div className="bp-field">
                <div className="bp-field-row">
                  <label className="bp-label">Nama</label>
                  <span className="bp-optional">(opsional)</span>
                </div>
                <input type="text" className="bp-input" placeholder="Nama lengkap Anda"
                  value={nama} onChange={(e) => setNama(e.target.value)} />
                <p className="bp-hint">Kosongkan jika ingin anonim</p>
              </div>
              <div className="bp-field">
                <div className="bp-field-row">
                  <label className="bp-label">Email / No. HP</label>
                  <span className="bp-optional">(opsional)</span>
                </div>
                <input type="text" className="bp-input" placeholder="Untuk notifikasi perkembangan laporan"
                  value={kontak} onChange={(e) => setKontak(e.target.value)} />
              </div>
            </div>
            <div className="bp-actions single">
              <button className="bp-btn-next" onClick={handleNext}>Lanjut ke Detail →</button>
            </div>
          </>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <>
            <div className="bp-card">
              <div className="bp-card-header">
                <span className="bp-card-accent" />
                <h2 className="bp-card-title">Detail Laporan</h2>
              </div>

              {errorMsg && <div className="bp-error-banner">⚠️ {errorMsg}</div>}

              <div className="bp-field">
                <div className="bp-field-row">
                  <label className="bp-label">Judul Laporan</label>
                  <span className="bp-required">*</span>
                </div>
                <input type="text" className="bp-input"
                  placeholder="Contoh: pelaporan kegiatan ToT"
                  maxLength={100} value={judul}
                  onChange={(e) => setJudul(e.target.value)} />
                <p className="bp-counter">{judul.length}/100</p>
              </div>

              <div className="bp-field">
                <div className="bp-field-row">
                  <label className="bp-label">Deskripsi</label>
                  <span className="bp-required">*</span>
                </div>
                <textarea className="bp-textarea" rows={5} maxLength={500}
                  placeholder="Jelaskan masalah secara detail: kapan terjadi, dampak yang dirasakan, sudah dicoba apa saja..."
                  value={deskripsi} onChange={(e) => setDeskripsi(e.target.value)} />
                <p className="bp-counter">{deskripsi.length}/500</p>
              </div>

              {/* ── FILE UPLOAD ── */}
              <div className="bp-field">
                <div className="bp-field-row">
                  <label className="bp-label">Lampiran</label>
                  <span className="bp-file-counter-badge">
                    {files.length}/{MAX_FILES} file
                  </span>
                </div>

                {/* Drop Zone */}
                <div
                  className={`bp-dropzone${dragOver ? " dragover" : ""}${files.length >= MAX_FILES ? " disabled" : ""}`}
                  onClick={() => files.length < MAX_FILES && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); if (files.length < MAX_FILES) setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  {files.length >= MAX_FILES ? (
                    <>
                      <div className="bp-dropzone-icon full">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                        </svg>
                      </div>
                      <p className="bp-dropzone-text">Batas {MAX_FILES} file tercapai</p>
                      <p className="bp-dropzone-hint">Hapus file untuk menambah yang baru</p>
                    </>
                  ) : (
                    <>
                      <div className={`bp-dropzone-icon${dragOver ? " animate" : ""}`}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <p className="bp-dropzone-text">
                        {dragOver ? "Lepaskan untuk mengunggah" : "Klik atau seret file ke sini"}
                      </p>
                      <p className="bp-dropzone-hint">JPG · PNG · PDF · ZIP · RAR · DOC · XLS · TXT</p>
                      <p className="bp-dropzone-hint">Maks. {MAX_FILE_SIZE_MB} MB per file</p>
                    </>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_TYPES.join(",")}
                  className="bp-file-input-hidden"
                  onChange={handleFileChange}
                />

                {/* Error per-file */}
                {fileErrors.length > 0 && (
                  <ul className="bp-file-errors">
                    {fileErrors.map((err, i) => (
                      <li key={i} className="bp-file-error-item">
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="7" stroke="#ef4444" strokeWidth="1.5"/>
                          <path d="M8 5v3.5M8 10.5v.5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {err}
                      </li>
                    ))}
                  </ul>
                )}

                {/* File List */}
                {files.length > 0 && (
                  <ul className="bp-file-list">
                    {files.map((f, i) => (
                      <FileItem
                        key={getFileKey(f)}
                        file={f}
                        index={i}
                        preview={previews[getFileKey(f)]}
                        onRemove={handleRemoveFile}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="bp-actions double">
              <button className="bp-btn-back" onClick={handleBack} disabled={isLoading}>← Kembali</button>
              <button className="bp-btn-next" onClick={handleSubmit}
                disabled={!judul || !deskripsi || isLoading}>
                {isLoading ? (
                  <span className="bp-loading-spinner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Mengirim...
                  </span>
                ) : "Kirim Laporan"}
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3 ── */}
        {step === 3 && (
          <div className="bp-card bp-success-card">
            <div className="bp-success-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="20" fill="#10b981" />
                <path d="M12 20l5.5 5.5L28 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="bp-success-title">Laporan Berhasil Dikirim!</h2>
            <p className="bp-success-desc">
              Laporan kamu telah kami terima dan akan segera diproses
              dalam maksimal 3 hari kerja.
            </p>
            <div className="bp-kode-box">
              <p className="bp-kode-label">Kode Tracking Laporan</p>
              <p className="bp-kode-value">{trackingCode}</p>
              <p className="bp-kode-hint">Simpan kode ini untuk memantau status laporan</p>
            </div>
            <div className="bp-success-actions">
              <button className={`bp-btn-copy ${copySuccess ? "copied" : ""}`} onClick={handleCopyCode}>
                {copySuccess ? "✓ Kode Disalin" : "Salin Kode"}
              </button>
              <button className="bp-btn-check-status" onClick={() => navigate("/cek-status")}>
                Cek Status Laporan →
              </button>
              <button className="bp-btn-create-new" onClick={handleCreateNew}>Buat Laporan Baru</button>
            </div>
            <button className="bp-btn-home" onClick={() => navigate("/")}>Kembali ke Beranda</button>
          </div>
        )}
      </main>
    </div>
  );
};

export default BuatPengaduan;